import { useState } from 'react';
import {
  Plus,
  MoreHorizontal,
  Loader2,
  DollarSign,
  Pause,
  Play,
  Unlink,
  Package,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import {
  useCampaignLinkedProducts,
  useUnlinkProduct,
  useUpdateCampaignProduct,
  type LinkedProduct,
} from '@/hooks/useCatalog';
import { Badge } from './ui/badge';
import { Card, CardContent, EmptyState, ErrorState } from '@mustauto/ui/web';
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mustauto/ui/web';
import { formatReais, formatNumber } from '@/lib/utils';
import { CatalogLinkModal } from './CatalogLinkModal';
import { CatalogProductModal } from './CatalogProductModal';

interface Props {
  campaignId: string;
}

export function CampaignProductsTab({ campaignId }: Props) {
  const { user } = useAuth();
  const pid = user?.partnerId ?? '';

  const { data: products, isLoading, isError, error, refetch } = useCampaignLinkedProducts(pid, campaignId);
  const unlinkMutation = useUnlinkProduct(pid, campaignId);
  const updateMutation = useUpdateCampaignProduct(pid, campaignId);

  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [priceOverrideFor, setPriceOverrideFor] = useState<string | null>(null);
  const [priceOverrideValue, setPriceOverrideValue] = useState('');
  const [menuOpen, setMenuOpen] = useState<string | null>(null);

  const linkedProductIds = new Set(
    (products ?? []).filter((p) => p.partnerProductId).map((p) => p.partnerProductId as string),
  );

  function handlePriceOverride(cp: LinkedProduct) {
    setPriceOverrideFor(cp.id);
    setPriceOverrideValue(cp.priceOverride ? String(Number(cp.priceOverride)) : '');
    setMenuOpen(null);
  }

  function savePriceOverride() {
    if (!priceOverrideFor) return;
    const val = priceOverrideValue.trim() ? parseFloat(priceOverrideValue) : null;
    updateMutation.mutate(
      { campaignProductId: priceOverrideFor, priceOverride: val },
      { onSuccess: () => setPriceOverrideFor(null) },
    );
  }

  function toggleActive(cp: LinkedProduct) {
    updateMutation.mutate({ campaignProductId: cp.id, active: !cp.active });
    setMenuOpen(null);
  }

  function handleUnlink(cp: LinkedProduct) {
    if (confirm(`Desvincular "${cp.name}" desta campanha? O produto permanece no catalogo.`)) {
      unlinkMutation.mutate(cp.id);
    }
    setMenuOpen(null);
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Info card */}
      <Card>
        <CardContent className="flex items-start gap-3 pt-4">
          <Package size={18} className="mt-0.5 text-primary shrink-0" />
          <div className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Catálogo vs. Campanha:</span>{' '}
            Os dados do produto (nome, foto, preço base) vêm do catálogo mestre.
            Aqui você pode definir um <strong>preço promocional</strong> ou <strong>pausar</strong> o produto
            apenas nesta campanha, sem afetar outras campanhas.
          </div>
        </CardContent>
      </Card>

      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {products?.length ?? 0} produtos vinculados
        </span>
        <button onClick={() => setLinkModalOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90">
          <Plus size={14} /> Vincular produto
        </button>
      </div>

      {/* Table */}
      {isError && (products?.length ?? 0) === 0 ? (
        <Card>
          <CardContent className="p-0">
            <ErrorState
              title="Não foi possível carregar os produtos vinculados"
              error={error}
              onRetry={() => void refetch()}
            />
          </CardContent>
        </Card>
      ) : (products?.length ?? 0) === 0 ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              compact
              icon={<Package size={26} />}
              title="Nenhum produto vinculado"
              description="Vincule produtos do catálogo mestre a esta campanha para o cliente ver preço e link de compra."
              action={{ label: 'Vincular produto', onClick: () => setLinkModalOpen(true) }}
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Foto</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead className="text-right">Preço</TableHead>
                  <TableHead>Prazo</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Cliques</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(products ?? []).map((cp) => (
                  <TableRow key={cp.id}>
                    <TableCell>
                      {cp.photoUrl ? (
                        <img src={cp.photoUrl} alt={cp.name} className="h-10 w-10 rounded-md object-cover" />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted text-xs text-muted-foreground">—</div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{cp.name}</TableCell>
                    <TableCell className="text-right">
                      <div>
                        <span className="font-medium">
                          {cp.effectivePrice ? formatReais(Number(cp.effectivePrice)) : '—'}
                        </span>
                        {cp.hasPriceOverride && (
                          <div className="text-xs text-amber-600 dark:text-amber-400">
                            Override (catalogo: {cp.catalogPrice ? formatReais(Number(cp.catalogPrice)) : '—'})
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {cp.deliveryOverride ? (
                        <span className="text-amber-600 dark:text-amber-400 text-sm">{cp.deliveryOverride}</span>
                      ) : cp.deliveryDaysMin != null ? (
                        `${cp.deliveryDaysMin}${cp.deliveryDaysMax ? `-${cp.deliveryDaysMax}` : ''} dias`
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      {cp.outOfStock ? (
                        <Badge variant="warning">Sem estoque</Badge>
                      ) : cp.active ? (
                        <Badge variant="success">Ativo</Badge>
                      ) : (
                        <Badge variant="secondary">Pausado</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{formatNumber(cp.clicks)}</TableCell>
                    <TableCell>
                      <div className="relative">
                        <button
                          onClick={() => setMenuOpen(menuOpen === cp.id ? null : cp.id)}
                          className="rounded p-1 hover:bg-muted"
                        >
                          <MoreHorizontal size={14} />
                        </button>
                        {menuOpen === cp.id && (
                          <div className="absolute right-0 top-8 z-20 w-48 rounded-lg border border-border bg-background py-1 shadow-lg">
                            <button onClick={() => handlePriceOverride(cp)}
                              className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-muted">
                              <DollarSign size={14} /> Preco promocional
                            </button>
                            <button onClick={() => toggleActive(cp)}
                              className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-muted">
                              {cp.active ? <><Pause size={14} /> Pausar nesta campanha</> : <><Play size={14} /> Ativar nesta campanha</>}
                            </button>
                            <button onClick={() => handleUnlink(cp)}
                              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-destructive/10">
                              <Unlink size={14} /> Desvincular
                            </button>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Price override mini-modal */}
      {priceOverrideFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" aria-hidden="true" onClick={() => setPriceOverrideFor(null)} />
          <div className="relative z-10 w-full max-w-xs rounded-xl bg-background p-4 shadow-xl space-y-3">
            <h3 className="text-sm font-bold">Preco promocional</h3>
            <p className="text-xs text-muted-foreground">
              Deixe vazio para usar o preco do catalogo
            </p>
            <input
              type="number"
              min={0.01}
              step={0.01}
              value={priceOverrideValue}
              onChange={(e) => setPriceOverrideValue(e.target.value)}
              placeholder="Preco override (R$)"
              className="w-full rounded-lg border border-border bg-muted px-3 py-2 text-sm outline-none focus:border-primary"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setPriceOverrideFor(null)}
                className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-muted">
                Cancelar
              </button>
              <button onClick={savePriceOverride} disabled={updateMutation.isPending}
                className="rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Link modal */}
      {linkModalOpen && (
        <CatalogLinkModal
          campaignId={campaignId}
          linkedProductIds={linkedProductIds}
          onClose={() => setLinkModalOpen(false)}
          onCreateNew={() => { setLinkModalOpen(false); setCreateModalOpen(true); }}
        />
      )}

      {/* Create new product modal */}
      {createModalOpen && (
        <CatalogProductModal
          onClose={() => setCreateModalOpen(false)}
          onCreated={() => { setCreateModalOpen(false); setLinkModalOpen(true); }}
        />
      )}
    </div>
  );
}
